<template>
  <Modal
    class="scoped"
    :modal-style="{ maxWidth: modalMaxWidth }"
    title="Import Briefs"
  >
    <div style="max-width: 100vw">
      <QCardSection class="row items-center">
        <h4>Import Briefs via CSV</h4>
      </QCardSection>

      <QCardSection v-if="step === 1">
        <div class="row">
          <QInput
            accept=".csv"
            class="full-width"
            filled
            hint="Upload csv"
            square
            type="file"
            @update:model-value="processCsvFile"
          />
        </div>
      </QCardSection>

      <QCardSection
        v-if="step === 1"
        align="right"
      >
        <VueJsonCsv
          class="col"
          :data="sampleBriefsCsvData"
          name="sample-briefs.csv"
        >
          <QBtn
            color="primary"
            flat
            label="Sample briefs.csv"
            no-caps
          />
        </VueJsonCsv>
      </QCardSection>

      <QCardSection v-if="step === 2">
        <div class="row">
          <QMarkupTable
            class="full-width scroll"
            style="height: 600px"
          >
            <thead>
              <tr>
                <th class="column-status text-left">Valid</th>
                <th class="col-label text-left">Label</th>
                <th class="column-label text-left">Group</th>
                <th class="column-label text-left">Brief Set</th>
                <th class="column-query text-left">Query</th>
                <th class="column-checkbox text-left">Case Sensitive</th>
                <th class="column-checkbox text-left">Print</th>
                <th class="column-checkbox text-left">Online</th>
                <th class="column-checkbox text-left">Magazine</th>
                <th class="column-checkbox text-left">TV</th>
                <th class="column-checkbox text-left">Radio</th>
                <th class="column-checkbox text-left">Social</th>
                <th class="column-checkbox text-left">Podcast</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(brief, index) in briefsToImport"
                :key="index"
                :class="{ 'brief-error': !brief.valid }"
              >
                <td class="column-status text-center">
                  <QSpinner v-if="brief.loading" />
                  <QIcon
                    v-if="!brief.loading"
                    size="sm"
                    v-bind="statusAttrubutes(brief)"
                  />
                </td>
                <td class="column-label text-left">
                  <QInput
                    v-model="brief.label"
                    filled
                    square
                  />
                </td>
                <td class="column-label text-left">
                  <QInput
                    v-model="brief.group"
                    filled
                    square
                  />
                </td>
                <td class="column-label text-left">
                  <QInput
                    v-model="brief.brief_set"
                    filled
                    square
                  />
                </td>
                <td class="column-query text-left">
                  <div class="q-pa-md">
                    <QBtn
                      dense
                      flat
                      :icon-right="
                        brief.expanded
                          ? 'keyboard_arrow_up'
                          : 'keyboard_arrow_down'
                      "
                      label="Show query"
                      no-caps
                      @click="brief.expanded = !brief.expanded"
                    />
                  </div>
                  <div
                    v-if="brief.expanded"
                    class="row full-width table-row"
                  >
                    <QueryEditor
                      :brief="brief"
                      class="full-width"
                      hide-controls
                      label=""
                      :media-types="types"
                      :model-value="brief.boolean_query"
                      show-error
                      @has-error="updateError(brief, $event)"
                    />
                  </div>
                  <p
                    v-if="!brief.valid"
                    class="text-ds-red-5"
                  >
                    {{ brief.validationMessage }}
                  </p>
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox v-model="brief.case_sensitive" />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox
                    v-model="brief.print_content"
                    class="color-print"
                  />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox
                    v-model="brief.online_content"
                    class="color-online"
                  />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox
                    v-model="brief.magazine_content"
                    class="color-magazine"
                  />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox
                    v-model="brief.tv_content"
                    class="color-tv"
                  />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox
                    v-model="brief.radio_content"
                    class="color-radio"
                  />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox
                    v-model="brief.social_content"
                    class="color-social"
                  />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox
                    v-model="brief.podcast_content"
                    class="color-podcast"
                  />
                </td>
              </tr>
            </tbody>
          </QMarkupTable>
        </div>
      </QCardSection>

      <QCardActions>
        <div class="full-width flex justify-between">
          <div>
            <QBtn
              v-if="step === 2"
              color="primary"
              flat
              label="Back"
              no-caps
              @click="step = 1"
            />
          </div>
          <div>
            <QBtn
              v-close-popup
              color="primary"
              flat
              label="Cancel"
              no-caps
              @click="cancel"
            />
            <QBtn
              v-if="step === 1"
              color="black"
              :disabled="validateDisabled"
              :label="validateLabel"
              no-caps
              @click="validate"
            />
            <QBtn
              v-if="step === 2"
              v-close-popup
              color="black"
              :disabled="importDisabled"
              :label="importLabel"
              no-caps
              @click="confirm"
            />
          </div>
        </div>
      </QCardActions>
    </div>
  </Modal>
</template>

<script>
import Papa from "papaparse";
import { storeToRefs } from "pinia";
import VueJsonCsv from "vue-json-csv";

import Modal from "shared/components/core/modals/Modal.vue";
import { QueryEditor } from "shared/components/QueryEditor";
import { streamTypes } from "shared/constants";
import { getSources, getTypesForSources } from "shared/helpers/media";
import { defaultLocationFilters } from "shared/helpers/stream";
import { pluralize } from "shared/helpers/string";

import { useGlobalAdminStore } from "src/stores/global";

export default {
  name: "ImportBriefsModal",
  components: {
    Modal,
    QueryEditor,
    VueJsonCsv,
  },
  props: {
    organisationId: {
      type: [Number, String],
      required: true,
    },
    briefGroups: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["cancel", "confirm"],
  setup() {
    const globalAdminStore = useGlobalAdminStore();
    const { locations, currentOrganisation } = storeToRefs(globalAdminStore);
    const { getLocations } = globalAdminStore;

    return {
      currentOrganisation,
      locations,
      getLocations,
    };
  },
  data() {
    return {
      step: 1,
      briefsToImport: [],
      sampleBriefsCsvData: [
        {
          label: "String - Required",
          group: "String - Required",
          boolean_query: "String - Required",
          case_sensitive: "TRUE/FALSE",
          print_content: "TRUE/FALSE",
          magazine_content: "TRUE/FALSE",
          online_content: "TRUE/FALSE",
          tv_content: "TRUE/FALSE",
          radio_content: "TRUE/FALSE",
          social_content: "TRUE/FALSE",
          podcast_content: "TRUE/FALSE",
          brief_set: "String - Optional",
        },
        {
          label: "My Stream",
          group: "My Group",
          boolean_query: "this OR that",
          case_sensitive: false,
          print_content: true,
          magazine_content: false,
          online_content: true,
          tv_content: true,
          radio_content: true,
          social_content: false,
          podcast_content: true,
          brief_set: "Brief Set Name",
        },
      ],
    };
  },
  computed: {
    validateDisabled() {
      return !this.organisationId || !this.briefsToImport.length;
    },
    importDisabled() {
      return !this.organisationId || !this.briefsToImport.length;
    },
    pluralString() {
      return `${this.briefsToImport.length} ${pluralize(
        this.briefsToImport.length,
        "Brief"
      )}`;
    },
    validateLabel() {
      return `Validate ${this.pluralString}`;
    },
    importLabel() {
      return `Import ${this.pluralString}`;
    },
    media() {
      return getSources().map(({ medium }) => medium);
    },
    types() {
      return getTypesForSources(getSources());
    },
    modalMaxWidth() {
      return this.step === 2 ? "100%" : "500px";
    },
  },
  async mounted() {
    try {
      await this.getLocations();
    } catch (error) {
      this.$error(
        "There was an error loading this page. Please try again.",
        error
      );
    }
  },
  methods: {
    processCsvFile([file]) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const csvFile = event.target.result.trim();
        const parsed = Papa.parse(csvFile).data;
        const columns = parsed.shift();

        this.briefsToImport = parsed.map((values) => {
          // set columns uploaded
          const brief = values.reduce((acc, value, index) => {
            let columnValue = value;
            if (["TRUE", "true"].includes(columnValue)) columnValue = true;
            if (["FALSE", "false"].includes(columnValue)) columnValue = false;

            if (value) acc[columns[index]] = columnValue;

            return acc;
          }, {});

          // set needed fields for import
          brief.organisation_id = this.organisationId;
          brief.query_style = "boolean";
          // set fields for validation
          brief.valid = false;
          brief.loading = false;
          brief.validationMessage = "";
          brief.expanded = false;

          return brief;
        });
      };

      reader.readAsText(file);
    },
    uploadBriefs() {
      const locationFilters = defaultLocationFilters(
        this.locations,
        getSources(),
        this.currentOrganisation
      );

      const briefParams = this.briefsToImport.map((brief) => {
        const group = this.briefGroups.find(
          ({ label }) => label === brief.group
        );

        const params = { ...brief };

        if (group && group.id) {
          delete params.group;
          params.group_id = group.id;
        } else {
          params.group = {
            label: brief.group,
            color: "#eee",
            organisation_id: brief.organisation_id,
            position: this.briefGroups.length,
            stream_type: streamTypes.organisationBrief,
          };
        }

        params.filters = locationFilters;

        return params;
      });

      return this.$streemApiAdmin.post("briefs/import", {
        params: { streams: briefParams },
      });
    },
    cancel() {
      this.$emit("cancel");
    },
    async validateQuery(brief) {
      try {
        Object.assign(brief, { loading: true });

        await this.$streemApiAdmin.post("search", {
          params: {
            limit: 0,
            query: brief.boolean_query,
            media: this.media,
          },
        });

        Object.assign(brief, { valid: true });
      } catch (error) {
        const message = error.response?.data?.error?.message
          ? error.response?.data?.error?.message
          : error.message;

        Object.assign(brief, {
          valid: false,
          validationMessage: `Error: ${message.replace('"\\\\"', '""')}`,
        });
      } finally {
        Object.assign(brief, { loading: false });
      }
    },
    validate() {
      this.step = 2;
      this.briefsToImport.forEach(this.validateQuery);
    },
    async confirm() {
      try {
        this.$q.loading.show({ delay: 500 });
        const result = (await this.uploadBriefs()).data;

        const failedBriefs = result.filter((briefStatus) => briefStatus.error);

        const failedRows = failedBriefs
          .map(
            (brief) => `<tr><td>${brief.label}</td><td>${brief.error}</td></tr>`
          )
          .join("\n");

        const successfulBriefs = result.length - failedBriefs.length;

        let message = `
          <strong>
            Sucessfully imported ${successfulBriefs} of ${this.pluralString}
          </strong>
        `;

        if (failedBriefs.length) {
          message += `
            <br/>
            <br/><strong>Failed Briefs:</strong><br/>
            <table class="q-table">
              <thead>
                <tr>
                  <td>Label</td>
                  <td>Fail Reason</td>
                </tr>
              </thead>
              <tbody>
                ${failedRows}
              </tbody>
            </table>
          `;
        }

        this.$q
          .dialog({
            html: true,
            title: "Import Complete",
            message,
            persistent: true,
            "full-width": Boolean(failedBriefs.length),
            ok: "Done",
          })
          .onOk(() => {
            this.cancel();
            this.$emit("confirm");
          });
      } catch (error) {
        this.$error("There was an error importing the briefs", error);
      } finally {
        this.$q.loading.hide();
      }
    },
    statusAttrubutes(brief) {
      return {
        name: brief.valid ? "check" : "error",
        color: brief.valid ? "green" : "red",
      };
    },
    updateError(brief, error) {
      Object.assign(brief, { valid: !error });
    },
  },
};
</script>

<style lang="scss" scoped>
:deep() {
  .column-status {
    width: 2%;
    min-width: 2%;
    max-width: 2%;
  }

  .column-label {
    width: 10%;
    min-width: 10%;
    max-width: 10%;
  }

  .column-query {
    width: 30%;
    min-width: 30%;
    max-width: 30%;
    padding: 5px;
  }

  .column-checkbox {
    width: 5%;
    min-width: 5%;
    max-width: 5%;
    padding: 0;
  }

  .brief-error {
    background-color: #ff78781f;
  }
}
</style>
